import {initializeApp} from 'firebase/app';
import {deleteToken, getMessaging, getToken} from 'firebase/messaging';

import log from '@/services/logger/log';
import PUSH_NOTIFICATION_CONFIG from '@/services/pushNotification/pushNotificationConfig';
import serviceWorkerService from '@/services/serviceWorkerService';

import notificationPermissionService from './notificationPermissionService';

let firebaseApp, firebaseMessaging;

const getFirebaseMessaging = () => {
    if (!firebaseApp) {
        firebaseApp = initializeApp(PUSH_NOTIFICATION_CONFIG.FIREBASE_CONFIG);
    }
    if (!firebaseMessaging) {
        firebaseMessaging = getMessaging(firebaseApp);
    }
    return firebaseMessaging;
};

// Native Browser Push Notification Provider
const nativeProvider = {
    sanitizeSubscription: (subscription) => {
        const result = subscription?.toJSON();

        return result;
    },
    subscribe: async () => {
        // Not implemented
    },
    unsubscribe: async () => {
        const serviceWorkerRegistration = await serviceWorkerService.registerServiceWorker();

        if (!serviceWorkerRegistration) {
            return;
        }

        const subscription = await serviceWorkerRegistration.pushManager.getSubscription();

        if (subscription) {
            await subscription.unsubscribe();
            log.info('Native push notification unsubscribed successfully');
        }
    },
    getCurrentSubscription: async () => {
        const serviceWorkerRegistration = await serviceWorkerService.registerServiceWorker();

        if (!serviceWorkerRegistration) {
            return null;
        }

        try {
            const subscription = await serviceWorkerRegistration.pushManager.getSubscription();
            const sanitizedSubscription = nativeProvider.sanitizeSubscription(subscription);

            return sanitizedSubscription;
        } catch (e) {
            log.info(`Error getting existing native subscription: ${e}`);
            return null;
        }
    },
};

// Firebase Push Notification Provider
const firebaseProvider = {
    subscribe: async () => {
        await notificationPermissionService.requestNotificationPermission();

        const serviceWorkerRegistration = await serviceWorkerService.registerServiceWorker();

        if (!serviceWorkerRegistration) {
            throw new Error('Service Worker registration failed');
        }

        const messaging = getFirebaseMessaging();
        const token = await getToken(messaging, {
            vapidKey: PUSH_NOTIFICATION_CONFIG.VAPID_PUBLIC_KEY,
            serviceWorkerRegistration,
        });

        if (!token) {
            throw new Error('Failed to get Firebase token');
        }

        log.info('Firebase push token received');

        return {token};
    },
    unsubscribe: async () => {
        const serviceWorkerRegistration = await serviceWorkerService.registerServiceWorker();

        if (!serviceWorkerRegistration) {
            return;
        }

        const messaging = getFirebaseMessaging();
        const token = await getToken(messaging, {
            vapidKey: PUSH_NOTIFICATION_CONFIG.VAPID_PUBLIC_KEY,
            serviceWorkerRegistration,
        });

        if (token) {
            try {
                await deleteToken(messaging);
                log.info('Firebase push notification unsubscribed successfully');
            } catch (error) {
                log.info(
                    `Error unsubscribing from Firebase push notification: ${error}. Unsubscribing native push notification`
                );
                await nativeProvider.unsubscribe();
            }
        }
    },
    getCurrentSubscription: async () => {
        try {
            const serviceWorkerRegistration = await serviceWorkerService.registerServiceWorker();

            if (!serviceWorkerRegistration) {
                return null;
            }

            // If there is no native subscription return null
            // because getToken will create a new token
            const nativeCurrentSubscription = await nativeProvider.getCurrentSubscription();

            if (!nativeCurrentSubscription) {
                return null;
            }

            const messaging = getFirebaseMessaging();
            const token = await getToken(messaging, {
                vapidKey: PUSH_NOTIFICATION_CONFIG.VAPID_PUBLIC_KEY,
                serviceWorkerRegistration,
            });

            return token ? {token} : null;
        } catch (e) {
            log.info(`Error getting existing Firebase subscription: ${e}`);
            return null;
        }
    },
};

const getProvider = () => {
    return isFirebaseProvider() ? firebaseProvider : nativeProvider;
};

const isFirebaseProvider = () => {
    return true;
};

export default {
    getProvider,
    isFirebaseProvider,
    nativeProvider,
};
